import React, { useState, useEffect } from 'react';
import { FileCheck, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { validateAndFetchArxivMetadata } from '@/lib/api/arxiv-metadata';

interface PaperLicenseData {
  id: string;
  title: string;
  abstract: string;
  external_url: string;
  uploaded_at: string;
  current_license: string | null;
}

interface RestrictiveLicensePaper {
  id: string;
  title: string;
  license: string;
  uploaded_at: string;
}

const LicenseRemediationOverview = () => {
  const [papers, setPapers] = useState<PaperLicenseData[]>([]);
  const [restrictivePapers, setRestrictivePapers] = useState<RestrictiveLicensePaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRestrictive, setLoadingRestrictive] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processingPapers, setProcessingPapers] = useState<Set<string>>(new Set());

  const fetchPapersNeedingLicenseRemediation = async () => {
    try {
      const { data, error } = await supabase.rpc('get_papers_needing_license_remediation');
      
      if (error) {
        console.error('Error fetching papers needing license remediation:', error);
        toast.error('Failed to fetch papers needing license remediation');
        return;
      }

      setPapers(data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while fetching papers');
    }
  };

  const fetchPapersWithRestrictiveLicenses = async () => {
    try {
      setLoadingRestrictive(true);
      
      // Query papers that have licenses containing restrictive terms
      const { data, error } = await supabase
        .from('papers')
        .select(`
          id,
          license,
          current_version,
          paper_versions!inner(title, published_at, version)
        `)
        .not('license', 'is', null)
        .neq('license', '')
        .or('license.ilike.%ND%,license.ilike.%no deriv%,license.ilike.%noderivs%,license.ilike.%no-deriv%,license.ilike.%CC BY-NC-ND%,license.ilike.%NonCommercial-NoDerivatives%');

      if (error) {
        console.error('Error fetching papers with restrictive licenses:', error);
        toast.error('Failed to fetch papers with restrictive licenses');
        return;
      }

      const restrictiveData = data?.map(paper => {
        // Find the current version from paper_versions
        const currentVersion = paper.paper_versions.find(v => v.version === paper.current_version) || paper.paper_versions[0];
        return {
          id: paper.id,
          title: currentVersion?.title || 'Unknown Title',
          license: paper.license || '',
          uploaded_at: currentVersion?.published_at || ''
        };
      }) || [];

      setRestrictivePapers(restrictiveData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred while fetching restrictive licenses');
    } finally {
      setLoadingRestrictive(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchPapersNeedingLicenseRemediation(),
      fetchPapersWithRestrictiveLicenses()
    ]);
    setRefreshing(false);
  };

  const extractLicenseFromArxiv = async (paper: PaperLicenseData) => {
    if (!paper.external_url) {
      toast.error('No arXiv URL found for this paper');
      return;
    }

    setProcessingPapers(prev => new Set(prev).add(paper.id));

    try {
      // Extract arXiv ID from URL - handle both arxiv.org and www.arxiv.org
      const arxivMatch = paper.external_url.match(/(?:www\.)?arxiv\.org\/(abs|pdf)\/(.+?)(?:\.pdf)?$/);
      if (!arxivMatch) {
        toast.error('Invalid arXiv URL format');
        return;
      }

      const arxivId = arxivMatch[2];
      
      // Use OAI-PMH interface to extract license
      const { data: result, error: functionError } = await supabase.functions.invoke('extract-arxiv-license', {
        body: { arxivId }
      });
      
      if (functionError) {
        console.error('Error calling license extraction function:', functionError);
        toast.error('Failed to extract license information');
        return;
      }

      if (!result?.success) {
        toast.error(`Failed to extract license: ${result?.error || 'Unknown error'}`);
        return;
      }

      const licenseValue = result?.license?.name || result?.license?.rawText || 'unknown';

      // Update the paper with the license information
      const { error: updateError } = await supabase
        .from('papers')
        .update({ license: licenseValue })
        .eq('id', paper.id);

      if (updateError) {
        console.error('Error updating paper license:', updateError);
        toast.error('Failed to update paper license');
        return;
      }

      if (licenseValue === 'unknown') {
        toast.info(`No license information found for "${paper.title}" - marked as unknown`);
      } else {
        toast.success(`License information updated for "${paper.title}": ${licenseValue}`);
      }
      
      // Remove this paper from the list since it now has license info
      setPapers(prev => prev.filter(p => p.id !== paper.id));

    } catch (error) {
      console.error('Error extracting license:', error);
      toast.error('Failed to extract license information');
    } finally {
      setProcessingPapers(prev => {
        const newSet = new Set(prev);
        newSet.delete(paper.id);
        return newSet;
      });
    }
  };

  const extractAllLicenses = async () => {
    if (papers.length === 0) return;

    const batchSize = 3; // Process 3 papers at a time to avoid rate limiting
    
    for (let i = 0; i < papers.length; i += batchSize) {
      const batch = papers.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(paper => extractLicenseFromArxiv(paper))
      );
      
      // Add a small delay between batches
      if (i + batchSize < papers.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  };

  const refetchAllLicenses = async () => {
    setRefreshing(true);
    
    try {
      // Get all papers that have external URLs (regardless of current license status)
      const { data: allPapers, error } = await supabase
        .from('papers')
        .select(`
          id,
          external_url,
          paper_versions!inner(title)
        `)
        .not('external_url', 'is', null)
        .neq('external_url', '')
        .ilike('external_url', '%arxiv.org%');

      if (error) {
        console.error('Error fetching papers for re-fetch:', error);
        toast.error('Failed to fetch papers for license re-fetch');
        return;
      }

      if (!allPapers || allPapers.length === 0) {
        toast.info('No arXiv papers found to re-fetch licenses for');
        return;
      }

      const papersToProcess = allPapers.map(p => ({
        id: p.id,
        title: p.paper_versions[0]?.title || 'Unknown Title',
        external_url: p.external_url,
        abstract: '',
        uploaded_at: '',
        current_license: null
      }));

      const batchSize = 3;
      let successCount = 0;
      
      for (let i = 0; i < papersToProcess.length; i += batchSize) {
        const batch = papersToProcess.slice(i, i + batchSize);
        
        const results = await Promise.allSettled(
          batch.map(async (paper) => {
            try {
              // Extract arXiv ID from URL - handle both arxiv.org and www.arxiv.org
              const arxivMatch = paper.external_url.match(/(?:www\.)?arxiv\.org\/(?:abs\/)?(?:pdf\/)?(\d+\.\d+)/);
              if (!arxivMatch) {
                return { success: false, title: paper.title };
              }

              const arxivId = arxivMatch[1];
              
              const { data, error } = await supabase.functions.invoke('extract-arxiv-license', {
                body: { arxivId }
              });

              if (error) {
                console.error('Error extracting license:', error);
                return { success: false, title: paper.title };
              }

              if (!data?.success) {
                console.error('License extraction failed:', data?.error);
                return { success: false, title: paper.title };
              }

              const license = data?.license?.name || data?.license?.rawText || 'unknown';
              
              const { error: updateError } = await supabase
                .from('papers')
                .update({ license })
                .eq('id', paper.id);

              if (!updateError) {
                successCount++;
                return { success: true, title: paper.title };
              }
              
              return { success: false, title: paper.title };
            } catch (error) {
              console.error('Error processing paper:', error);
              return { success: false, title: paper.title };
            }
          })
        );
        
        // Add a small delay between batches
        if (i + batchSize < papersToProcess.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      toast.success(`Successfully re-fetched licenses for ${successCount} papers`);
      
      // Refresh all data
      await Promise.all([
        fetchPapersNeedingLicenseRemediation(),
        fetchPapersWithRestrictiveLicenses()
      ]);

    } catch (error) {
      console.error('Error re-fetching licenses:', error);
      toast.error('Failed to re-fetch licenses');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchPapersNeedingLicenseRemediation(),
        fetchPapersWithRestrictiveLicenses()
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
        <div>
          <h2 className="text-2xl font-bold">License Remediation</h2>
          <p className="text-muted-foreground">
            Extract license information from arXiv for papers missing this data
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap md:flex-nowrap">
          {papers.length > 0 && (
            <Button 
              onClick={extractAllLicenses}
              disabled={processingPapers.size > 0}
              className="gap-1 flex-shrink-0"
            >
              <FileCheck className="h-4 w-4" />
              Extract All Licenses
            </Button>
          )}
          <Button 
            onClick={refetchAllLicenses}
            disabled={refreshing}
            variant="outline"
            className="gap-1 flex-shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Re-fetch All Licenses
          </Button>
          <Button 
            onClick={refreshData} 
            disabled={refreshing} 
            size="sm" 
            className="gap-1 flex-shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Papers Missing License Information
            <Badge variant="outline" className="ml-2">
              {loading ? '...' : papers.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : papers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileCheck className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>All papers with arXiv URLs have license information!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>arXiv URL</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {papers.map((paper) => (
                  <TableRow key={paper.id}>
                    <TableCell className="font-medium truncate max-w-[300px]">
                      {paper.title}
                    </TableCell>
                    <TableCell className="truncate max-w-[200px]">
                      <a 
                        href={paper.external_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {paper.external_url}
                      </a>
                    </TableCell>
                    <TableCell>
                      {new Date(paper.uploaded_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        disabled={processingPapers.has(paper.id)}
                        onClick={() => extractLicenseFromArxiv(paper)}
                      >
                        {processingPapers.has(paper.id) ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <FileCheck className="h-4 w-4 mr-1" />
                            Extract License
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Papers with Restrictive Licenses (No Derivatives)
            <Badge variant="outline" className="ml-2">
              {loadingRestrictive ? '...' : restrictivePapers.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          {loadingRestrictive ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : restrictivePapers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileCheck className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>No papers found with restrictive licenses that prohibit derivatives!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Uploaded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {restrictivePapers.map((paper) => (
                  <TableRow key={paper.id}>
                    <TableCell className="font-medium truncate max-w-[300px]">
                      {paper.title}
                    </TableCell>
                    <TableCell className="truncate max-w-[250px]">
                      <Badge variant="destructive" className="font-mono text-xs">
                        {paper.license}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(paper.uploaded_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LicenseRemediationOverview;